import React, { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setCartItem } from "../../redux/features/cartSlice";
import {
  useBuildBomCartItemsMutation,
  useRematchBomMutation,
  useUpdateBomItemMutation,
  useUploadBomMutation,
} from "../../redux/api/bomApi";
import UniverBomSheet, {
  MATCH_STATUS,
  STATUS_LABELS,
  extractBomSheetRows,
  formatMoney,
} from "./UniverBomSheet";
import "./BomOrderPage.css";

/**
 * 获取 BOM 本地会话 ID，游客上传时用于访问自己的 BOM 任务。
 * @returns {string} BOM 会话 ID
 */
const getBomSessionId = () => {
  const storageKey = "bomOrderSessionId";
  const existingId = window.localStorage.getItem(storageKey);
  if (existingId) return existingId;

  const nextId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  window.localStorage.setItem(storageKey, nextId);
  return nextId;
};

/**
 * 判断 BOM 明细是否允许加入购物车。
 * @param {Object} item BOM 明细
 * @returns {boolean} 是否允许选择
 */
const canSelectBomItem = (item = {}) => {
  return item.matchStatus === "matched" && item.selectedProduct && !item.needQuote;
};

/**
 * 计算 BOM 完全匹配率。
 * @param {Object} summary BOM 汇总信息
 * @returns {number} 匹配率
 */
const getMatchRate = (summary = {}) => {
  const totalRows = Number(summary.totalRows || 0);
  if (!totalRows) return 0;
  return Math.round((Number(summary.matchedRows || 0) / totalRows) * 1000) / 10;
};

const BomOrderPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const univerApiRef = useRef(null);
  const [sessionId] = useState(getBomSessionId);
  const [bomOrder, setBomOrder] = useState(null);
  const [activeStatuses, setActiveStatuses] = useState(new Set(MATCH_STATUS));
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [purchaseSets, setPurchaseSets] = useState(1);
  const [onlySelected, setOnlySelected] = useState(false);
  const [uploadBom, { isLoading: isUploading }] = useUploadBomMutation();
  const [updateBomItem] = useUpdateBomItemMutation();
  const [rematchBom, { isLoading: isRematching }] = useRematchBomMutation();
  const [buildBomCartItems, { isLoading: isAdding }] = useBuildBomCartItemsMutation();

  const summary = bomOrder?.summary || {};
  const matchRate = getMatchRate(summary);

  const filteredItems = useMemo(() => {
    const items = bomOrder?.items || [];
    return items.filter((item) => {
      const statusMatched = activeStatuses.has(item.matchStatus);
      const selectedMatched = !onlySelected || selectedItemIds.has(item._id);
      return statusMatched && selectedMatched;
    });
  }, [activeStatuses, bomOrder?.items, onlySelected, selectedItemIds]);

  /**
   * 用后端返回的 BOM 任务刷新当前表格和默认选中项。
   * @param {Object|null} nextBomOrder BOM 任务
   */
  const refreshBomOrder = (nextBomOrder) => {
    setBomOrder(nextBomOrder || null);
    setSelectedItemIds(
      new Set((nextBomOrder?.items || []).filter((item) => item.selected).map((item) => item._id))
    );
  };

  /**
   * 记录 Univer API，供保存表格编辑时读取当前工作簿。
   * @param {Object|null} univerAPI Univer facade API
   */
  const handleUniverReady = useCallback((univerAPI) => {
    univerApiRef.current = univerAPI;
  }, []);

  /**
   * 上传 BOM 文件并进入在线表格。
   * @param {File} file BOM 文件
   */
  const handleUploadFile = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("sessionId", sessionId);

    try {
      const response = await uploadBom(formData).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success("BOM 已生成在线表格");
    } catch (error) {
      toast.error(error?.data?.message || "BOM 上传失败");
    }
  };

  /**
   * 处理文件输入框变化。
   * @param {Event} event 输入事件
   */
  const handleFileChange = (event) => {
    handleUploadFile(event.target.files?.[0]);
    event.target.value = "";
  };

  /**
   * 触发选择 BOM 文件。
   */
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  /**
   * 切换匹配状态筛选。
   * @param {string} status 匹配状态
   */
  const toggleStatus = (status) => {
    const nextStatuses = new Set(activeStatuses);
    if (nextStatuses.has(status)) {
      nextStatuses.delete(status);
    } else {
      nextStatuses.add(status);
    }
    setActiveStatuses(nextStatuses);
  };

  /**
   * 切换全部状态筛选。
   * @param {boolean} checked 是否全选
   */
  const toggleAllStatus = (checked) => {
    setActiveStatuses(new Set(checked ? MATCH_STATUS : []));
  };

  /**
   * 切换当前筛选范围内可加购明细的全选状态。
   * @param {boolean} checked 是否全选
   */
  const toggleVisibleSelection = async (checked) => {
    const selectableItems = filteredItems.filter(canSelectBomItem);
    const nextIds = new Set(selectedItemIds);
    selectableItems.forEach((item) => {
      if (checked) nextIds.add(item._id);
      else nextIds.delete(item._id);
    });
    setSelectedItemIds(nextIds);

    try {
      await Promise.all(
        selectableItems.map((item) =>
          updateBomItem({
            bomId: bomOrder._id,
            itemId: item._id,
            sessionId,
            payload: { selected: checked },
          }).unwrap()
        )
      );
    } catch (error) {
      toast.error(error?.data?.message || "批量选择保存失败");
    }
  };

  /**
   * 按采购套数批量刷新购买数量。
   */
  const applyPurchaseSets = async () => {
    if (!bomOrder?._id) return;
    const safeSets = Math.max(1, Number(purchaseSets || 1));

    try {
      const responses = await Promise.all(
        (bomOrder.items || []).map((item) => {
          const unitQty = Number(item.normalized?.unitQty || item.original?.unitQty || 1);
          const spareQty = Number(item.normalized?.productionSurplusQty || 0);
          const quantity = unitQty * safeSets + spareQty;

          return updateBomItem({
            bomId: bomOrder._id,
            itemId: item._id,
            sessionId,
            payload: {
              quantity,
              normalized: {
                orderQty: quantity,
              },
            },
          }).unwrap();
        })
      );
      refreshBomOrder(responses[responses.length - 1]?.bomOrder || bomOrder);
      toast.success("采购套数已应用");
    } catch (error) {
      toast.error(error?.data?.message || "采购套数应用失败");
    }
  };

  /**
   * 保存 Univer 表格中的可编辑 BOM 字段。
   * @param {Object} options 保存选项
   * @param {boolean} options.silent 是否静默保存
   * @returns {Promise<boolean>} 是否保存成功
   */
  const saveSheetEdits = async ({ silent = false } = {}) => {
    if (!bomOrder?._id || !univerApiRef.current) return true;

    const patches = extractBomSheetRows(univerApiRef.current, filteredItems);
    if (patches.length === 0) return true;

    try {
      const responses = await Promise.all(
        patches.map((patch) =>
          updateBomItem({
            bomId: bomOrder._id,
            itemId: patch.itemId,
            sessionId,
            payload: {
              selected: patch.selected,
              normalized: patch.normalized,
            },
          }).unwrap()
        )
      );
      refreshBomOrder(responses[responses.length - 1]?.bomOrder || bomOrder);
      if (!silent) toast.success("表格编辑已保存");
      return true;
    } catch (error) {
      toast.error(error?.data?.message || "表格编辑保存失败");
      return false;
    }
  };

  /**
   * 重新匹配当前 BOM 任务。
   */
  const handleRematch = async () => {
    if (!bomOrder?._id) return;

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await rematchBom({ bomId: bomOrder._id, sessionId }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success("BOM 已重新匹配");
    } catch (error) {
      toast.error(error?.data?.message || "重新匹配失败");
    }
  };

  /**
   * 将已选 BOM 明细批量加入现有购物车。
   */
  const addSelectedToCart = async () => {
    if (!bomOrder?._id || selectedItemIds.size === 0) return;

    try {
      const response = await buildBomCartItems({
        bomId: bomOrder._id,
        sessionId,
        itemIds: Array.from(selectedItemIds),
      }).unwrap();

      (response?.cartItems || []).forEach((cartItem) => dispatch(setCartItem(cartItem)));
      toast.success(`${response?.cartItems?.length || 0} 个 BOM 物料已加入购物车`);
      navigate("/cart");
    } catch (error) {
      toast.error(error?.data?.message || "加入购物车失败");
    }
  };

  const visibleSelectableItems = filteredItems.filter(canSelectBomItem);
  const allVisibleSelected = visibleSelectableItems.length > 0
    && visibleSelectableItems.every((item) => selectedItemIds.has(item._id));

  return (
    <main className={`bom-order-page ${bomOrder ? "is-sheet-mode" : ""}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        className="bom-hidden-file"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      {!bomOrder ? (
        <div className="bom-upload-panel">
          <div className="bom-upload-header">
            <div className="bom-upload-icon">X</div>
            <div>
              <h1>BOM智能云表</h1>
              <p>上传 BOM 后自动生成在线表格，可编辑、查看匹配结果并加入购物车。</p>
            </div>
          </div>
          <div className="bom-feature-grid">
            <div><strong>智能极速配单</strong><span>优先按 C 编码和型号匹配</span></div>
            <div><strong>库存价格明细</strong><span>展示库存、阶梯价和小计</span></div>
            <div><strong>类 Excel 操作体验</strong><span>生成表格后可在线编辑</span></div>
            <div><strong>确认后加购</strong><span>复用当前项目购物车流程</span></div>
          </div>
          <button type="button" className="bom-primary-button" onClick={openFilePicker} disabled={isUploading}>
            {isUploading ? "上传中..." : "立即上传BOM表"}
          </button>
          <p className="bom-upload-help">支持 xls、xlsx、csv；最大文件不能超过 10M</p>
        </div>
      ) : (
        <div className="bom-sheet-workspace">
          <header className="bom-sheet-topbar">
            <div className="bom-sheet-left-actions">
              <button type="button" className="bom-icon-button" title="返回上传" onClick={() => setBomOrder(null)}>
                □
              </button>
              <button type="button" className="bom-icon-button" title="上传新 BOM" onClick={openFilePicker}>
                +
              </button>
            </div>
            <h1>{bomOrder.originalFileName || "BOM Online Sheet"}</h1>
            <div className="bom-sheet-stats">
              <div className="bom-match-card">
                <span className="bom-match-ring" style={{ "--match-rate": `${matchRate}%` }} />
                <strong>{matchRate}%</strong>
                <small>完全匹配率</small>
              </div>
              <label className="bom-purchase-sets">
                <span>采购套数</span>
                <input
                  type="number"
                  min="1"
                  value={purchaseSets}
                  onChange={(event) => setPurchaseSets(event.target.value)}
                />
                <em>套</em>
              </label>
            </div>
          </header>

          <section className="bom-sheet-menu">
            <nav>
              <button type="button">开始</button>
              <button type="button">视图</button>
              <button type="button" className="is-active">筛选</button>
            </nav>
            <div className="bom-sheet-actions">
              <button type="button" onClick={applyPurchaseSets}>采购偏好</button>
              <button type="button" onClick={() => saveSheetEdits()}>保存编辑</button>
              <button type="button" onClick={handleRematch} disabled={isRematching}>
                {isRematching ? "匹配中..." : "重新匹配"}
              </button>
              <button type="button" onClick={() => toast.info("TODO: 后续接入导出报价单")}>导出报价单</button>
              <button type="button" onClick={() => toast.info("TODO: 后续接入自定义表头")}>自定义表头</button>
            </div>
          </section>

          <section className="bom-sheet-filterbar">
            <div className="bom-filter-tool">
              <strong>A-Z</strong>
              <span>全局筛选</span>
            </div>
            <div className="bom-filter-tool">
              <strong>⊙</strong>
              <span>匹配结果筛选</span>
            </div>
            <label>
              <input
                type="checkbox"
                checked={activeStatuses.size === MATCH_STATUS.length}
                onChange={(event) => toggleAllStatus(event.target.checked)}
              />
              全选
            </label>
            {MATCH_STATUS.map((status) => (
              <label key={status}>
                <input
                  type="checkbox"
                  checked={activeStatuses.has(status)}
                  onChange={() => toggleStatus(status)}
                />
                {STATUS_LABELS[status]}
              </label>
            ))}
          </section>

          <section className="bom-univer-wrap">
            <UniverBomSheet bomOrder={bomOrder} items={filteredItems} onReady={handleUniverReady} />
          </section>

          <footer className="bom-sheet-footer">
            <div className="bom-footer-left">
              <button type="button" className="bom-trash-button" onClick={() => setSelectedItemIds(new Set())}>
                □
              </button>
              <label>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(event) => toggleVisibleSelection(event.target.checked)}
                />
                全选
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={onlySelected}
                  onChange={(event) => setOnlySelected(event.target.checked)}
                />
                只看选中
              </label>
              <span>已勾选{selectedItemIds.size}行</span>
              <span>共{summary.totalRows || 0}种物料</span>
            </div>
            <div className="bom-footer-summary">
              <span>共 {summary.totalRows || 0} 行</span>
              <span>完全匹配 {summary.matchedRows || 0} 行</span>
              <span>待确认 {summary.pendingRows || 0} 行</span>
              <span>无法匹配 {summary.unmatchedRows || 0} 行</span>
            </div>
            <div className="bom-footer-actions">
              <div>
                <span>总价：</span>
                <strong>${formatMoney(summary.totalAmount)}</strong>
              </div>
              <button type="button" className="bom-primary-button" onClick={addSelectedToCart} disabled={isAdding || selectedItemIds.size === 0}>
                {isAdding ? "加入中..." : "加入购物车"}
              </button>
            </div>
          </footer>
        </div>
      )}
    </main>
  );
};

export default BomOrderPage;
